import { createContext, useContext, useState, useEffect, useRef } from 'react';

/**
 * Contexto de Timer Global para componentes de tempo
 * 
 * Em vez de cada componente ter seu próprio setInterval,
 * usamos um único timer global que emite "ticks" a cada segundo.
 * 
 * Isso evita:
 * - Múltiplos setInterval (1 por linha da tabela = 20+ timers)
 * - Problemas quando a aba está em background (throttling)
 * - Re-renders cascata quando a aba volta ao foco
 */

const GlobalTimerContext = createContext(null);

export function GlobalTimerProvider({ children }) {
  // Timestamp atual - atualizado a cada segundo
  const [currentTime, setCurrentTime] = useState(Date.now());
  // Flag se a aba está visível
  const [isVisible, setIsVisible] = useState(!document.hidden);
  
  const intervalRef = useRef(null);
  const lastTickRef = useRef(Date.now());

  // Handler para mudança de visibilidade da aba
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsVisible(visible);
      
      if (visible) {
        // Quando a aba volta ao foco, atualiza o tempo imediatamente
        // mas de forma suave (não dispara múltiplos updates)
        setCurrentTime(Date.now());
        lastTickRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Timer global - só roda quando a aba está visível
  useEffect(() => {
    if (!isVisible) {
      // Aba em background - para o timer para economizar recursos
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Aba visível - inicia/reinicia o timer
    const tick = () => {
      const now = Date.now();
      // Evita ticks muito próximos (debounce de 500ms)
      if (now - lastTickRef.current >= 500) {
        lastTickRef.current = now;
        setCurrentTime(now);
      }
    };

    // Tick inicial
    tick();
    
    // Timer a cada segundo
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible]);

  return (
    <GlobalTimerContext.Provider value={{ currentTime, isVisible }}>
      {children}
    </GlobalTimerContext.Provider>
  );
}

/**
 * Hook para usar o timer global
 * Retorna o timestamp atual que é atualizado a cada segundo
 */
export function useGlobalTimer() {
  const context = useContext(GlobalTimerContext);
  
  if (!context) {
    // Fallback se não houver provider - usa Date.now() estático
    // (não atualiza automaticamente, mas não quebra)
    return { currentTime: Date.now(), isVisible: true };
  }
  
  return context;
}

/**
 * Hook para calcular tempo decorrido desde uma data
 * Usa o timer global para atualizações sincronizadas
 */
export function useElapsedTime(startDate) {
  const { currentTime, isVisible } = useGlobalTimer();
  
  if (!startDate) {
    return { elapsed: 0, isVisible };
  }

  const start = new Date(startDate).getTime();
  const elapsed = Math.max(0, currentTime - start);
  
  return { elapsed, isVisible };
}
